/**
 * Détection des vignettes — les VUES d'un plan (intrados, élévation, piles…)
 * posées sur une même feuille DWG.
 *
 * Chaque bureau d'études a sa mise en page : la détection est donc une
 * CASCADE DE SIGNAUX indépendants, chacun optionnel :
 *
 * 1. CADRES — beaucoup de feuilles encadrent chaque vue (cellules d'un
 *    tableau « Report désordres », cadres pointillés…). On repère les
 *    polylignes quasi rectangulaires du fond et on coupe leur arbre de
 *    contenance au niveau où un cadre en PAVE plusieurs autres : les cadres
 *    feuilles de cette coupe sont des vignettes. Un rectangle de contenu
 *    (une pile dessinée) ne découpe rien : ses frères ne pavent pas leur
 *    parent.
 * 2. AMAS SPATIAUX — pour tout le fond hors cadres (ou toute la feuille
 *    quand il n'y a aucun cadre) : rastérisation des emprises sur une grille
 *    grossière, composantes connexes = vignettes. C'est le signal des mises
 *    en page « vues espacées sans cadres ».
 *
 * Le titre d'une vignette est le plus grand texte du fond dans (ou contre)
 * son emprise. Tout est pur et bon marché : seulement des bbox et quelques
 * périmètres collectés par le scan en une traversée. Sans aucun signal, la
 * feuille entière reste l'échelle de référence (comportement historique).
 */

import { bboxDiagonal, bboxGap, bboxUnion } from "./geometry";
import type {
  AnalysisEntity,
  BackgroundItem,
  PlanVignette,
  WorldBbox,
} from "./types";

/**
 * ÉCHELLE DE FEUILLE des seuils relatifs : la moyenne géométrique √(L×H),
 * PAS la diagonale — sur une bande très allongée (vécu : 5485×284), la
 * diagonale ≈ la largeur et les seuils dépassent la hauteur de la feuille.
 */

/** Pas de la grille de rastérisation, en part de l'échelle de feuille. */
export const VIGNETTE_CELL_RATIO = 0.02;
/**
 * Élément FILANT : s'étend sur plus de cette part d'un axe de la feuille EN
 * ÉTANT FIN sur l'autre (ligne de sol, chaînage, MTEXT à colonne fantôme) —
 * il ponte les vues entre elles : mobilier de feuille, ignoré. Un élément
 * long mais ÉPAIS (cadre de vue dominant) n'est pas un filant.
 */
export const VIGNETTE_FRAME_RATIO = 0.6;
/** Finesse d'un filant : petite dimension ≤ cette part de la grande. */
export const SPANNER_THINNESS_RATIO = 0.05;
/** Un candidat cadre quasi égal au CONTENU entier est un bord de feuille. */
export const FRAME_FULL_CONTENT_RATIO = 0.9;
/** Le clustering ignore les boîtes quasi égales au contenu (bords, cadres). */
export const CLUSTER_FULL_CONTENT_RATIO = 0.75;
/** Diagonale minimale d'une vignette (part de l'échelle) — élimine les tampons isolés. */
export const MIN_VIGNETTE_DIAGONAL_RATIO = 0.04;
/** Marge de recherche du titre autour d'une vignette (part de l'échelle). */
export const VIGNETTE_CAPTION_MARGIN_RATIO = 0.03;
/**
 * Deux vignettes dont les emprises se recouvrent au-delà de ce ratio
 * (intersection / plus petite aire) sont une seule vue : fusionnées.
 */
export const VIGNETTE_MERGE_OVERLAP = 0.3;
/** Marge d'appartenance d'une entité à une vignette (part de la feuille). */
export const VIGNETTE_CONTAIN_MARGIN_RATIO = 0.01;
/**
 * Tolérance du test « quasi rectangle » : écart admis entre le périmètre
 * mesuré d'une polyligne et le périmètre de sa bbox.
 */
export const FRAME_PERIMETER_TOLERANCE = 0.15;
/**
 * Un cadre n'est DÉCOUPÉ en ses enfants que s'ils le pavent : somme de leurs
 * aires ≥ ce ratio de la sienne. Deux rectangles de contenu perdus dans une
 * vue (~10 % de couverture) ne déclenchent pas de découpage ; des cadres de
 * vues dans un tableau (≥ ~25 %) oui. À calibrer sur fixtures réelles.
 */
export const FRAME_TILE_COVER_RATIO = 0.2;
/** Plus petit côté admis pour un cadre (part de l'échelle de feuille). */
export const MIN_FRAME_SIDE_RATIO = 0.015;
/**
 * Un cadre de VUE contient du contenu (le dessin de la vue) : au moins ce
 * nombre d'éléments de fond. Un cartouche de titre ne contient que son
 * texte — ce n'est pas une vue.
 */
export const FRAME_MIN_CONTENT_ITEMS = 3;
/**
 * Assemblage de cadres depuis des SEGMENTS : beaucoup de grilles de vues
 * sont dessinées en lignes partagées (pas en polylignes fermées). Épaisseur
 * max d'un segment et tolérance d'alignement, en part de l'échelle.
 */
export const SEGMENT_THICKNESS_RATIO = 0.01;
export const SEGMENT_ALIGN_TOLERANCE_RATIO = 0.012;
/** Garde-fou de complexité : au-delà, pas d'assemblage (O(H²×V)). */
const MAX_SEGMENTS_FOR_ASSEMBLY = 800;
/**
 * Un texte est « de titre » dans un amas s'il atteint la moitié du PLUS
 * GRAND texte de l'amas (les cotes sont bien plus petites que les titres),
 * avec ce plancher absolu (part de l'échelle de feuille) pour les amas sans
 * vrais titres. Un amas à PLUSIEURS titres contient plusieurs vues — il est
 * re-clusterisé à son échelle locale.
 */
export const CAPTION_TEXT_FLOOR_RATIO = 0.01;
const CAPTION_RELATIVE_HEIGHT = 0.5;
/** Profondeur max du raffinement récursif des amas. */
const MAX_REFINE_DEPTH = 3;
/**
 * Plafond de cellules rastérisées par emprise : au-delà, seul le périmètre
 * est marqué (la connexité intérieure est rattrapée par la fusion par
 * recouvrement). Garde le scan linéaire sur les gros fonds.
 */
const MAX_CELLS_PER_ITEM = 2000;

function overlapRatio(a: WorldBbox, b: WorldBbox): number {
  const w = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const h = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  if (w <= 0 || h <= 0) return 0;
  const areaA = (a.maxX - a.minX) * (a.maxY - a.minY);
  const areaB = (b.maxX - b.minX) * (b.maxY - b.minY);
  const smallest = Math.min(areaA, areaB);
  if (smallest <= 0) return 1;
  return (w * h) / smallest;
}

function intersects(a: WorldBbox, b: WorldBbox): boolean {
  return (
    Math.min(a.maxX, b.maxX) > Math.max(a.minX, b.minX) &&
    Math.min(a.maxY, b.maxY) > Math.max(a.minY, b.minY)
  );
}

function intersectionArea(a: WorldBbox, b: WorldBbox): number {
  const w = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const h = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  return w > 0 && h > 0 ? w * h : 0;
}

// ── Signal 1 : cadres rectangulaires ────────────────────────────────────────

interface RectNode {
  bbox: WorldBbox;
  area: number;
  children: RectNode[];
}

/** `child` est-il strictement DANS `parent` (tolérance de bord comprise) ? */
function containsRect(
  parent: RectNode,
  child: RectNode,
  epsilon: number,
): boolean {
  return (
    child.area < parent.area * 0.98 &&
    child.bbox.minX >= parent.bbox.minX - epsilon &&
    child.bbox.minY >= parent.bbox.minY - epsilon &&
    child.bbox.maxX <= parent.bbox.maxX + epsilon &&
    child.bbox.maxY <= parent.bbox.maxY + epsilon
  );
}

interface AxisSegment {
  /** Étendue le long de l'axe du segment. */
  from: number;
  to: number;
  /** Position sur l'axe perpendiculaire. */
  at: number;
}

/**
 * Rectangles assemblés depuis les segments FINS du fond : deux horizontaux
 * de même étendue x à des y différents, fermés par un vertical à chaque
 * bout. C'est la forme des grilles de vues dessinées en lignes partagées
 * (chaque bord de cellule est un segment, parfois commun à deux cellules).
 */
function assembleSegmentRects(
  items: BackgroundItem[],
  sheetScale: number,
): WorldBbox[] {
  const thickness = SEGMENT_THICKNESS_RATIO * sheetScale;
  const tolerance = SEGMENT_ALIGN_TOLERANCE_RATIO * sheetScale;
  const minSpan = MIN_FRAME_SIDE_RATIO * sheetScale;
  const horizontal: AxisSegment[] = [];
  const vertical: AxisSegment[] = [];
  for (const it of items) {
    if (it.text) continue;
    const w = it.bbox.maxX - it.bbox.minX;
    const h = it.bbox.maxY - it.bbox.minY;
    if (h <= thickness && w >= minSpan) {
      horizontal.push({
        from: it.bbox.minX,
        to: it.bbox.maxX,
        at: (it.bbox.minY + it.bbox.maxY) / 2,
      });
    } else if (w <= thickness && h >= minSpan) {
      vertical.push({
        from: it.bbox.minY,
        to: it.bbox.maxY,
        at: (it.bbox.minX + it.bbox.maxX) / 2,
      });
    }
  }
  if (
    horizontal.length === 0 ||
    vertical.length === 0 ||
    horizontal.length > MAX_SEGMENTS_FOR_ASSEMBLY ||
    vertical.length > MAX_SEGMENTS_FOR_ASSEMBLY
  ) {
    return [];
  }

  const rects: WorldBbox[] = [];
  for (let i = 0; i < horizontal.length; i++) {
    for (let j = i + 1; j < horizontal.length; j++) {
      const a = horizontal[i];
      const b = horizontal[j];
      if (
        Math.abs(a.from - b.from) > tolerance ||
        Math.abs(a.to - b.to) > tolerance
      ) {
        continue;
      }
      const y1 = Math.min(a.at, b.at);
      const y2 = Math.max(a.at, b.at);
      if (y2 - y1 < minSpan) continue;
      const hasSide = (x: number): boolean =>
        vertical.some(
          (v) =>
            Math.abs(v.at - x) <= tolerance &&
            v.from <= y1 + tolerance &&
            v.to >= y2 - tolerance,
        );
      if (!hasSide(a.from) || !hasSide(a.to)) continue;
      rects.push({ minX: a.from, minY: y1, maxX: a.to, maxY: y2 });
    }
  }
  return rects;
}

/**
 * Vignettes issues des cadres : candidats quasi rectangulaires → arbre de
 * contenance → coupe au premier niveau de PAVAGE. Une chaîne (cellule qui ne
 * contient qu'un cadre pointillé) n'est pas découpée : c'est le cadre
 * EXTÉRIEUR de la chaîne qui fait vignette (son titre est dedans).
 */
function frameVignetteBoxes(
  items: BackgroundItem[],
  sheetScale: number,
  contentBbox: WorldBbox,
): WorldBbox[] {
  const minSide = MIN_FRAME_SIDE_RATIO * sheetScale;
  const contentW = contentBbox.maxX - contentBbox.minX;
  const contentH = contentBbox.maxY - contentBbox.minY;
  // Un rectangle quasi égal au contenu entier est un bord de feuille, pas
  // une vue — il contiendrait tout.
  const isFullContent = (w: number, h: number): boolean =>
    w >= FRAME_FULL_CONTENT_RATIO * contentW &&
    h >= FRAME_FULL_CONTENT_RATIO * contentH;
  const rects: RectNode[] = [];
  for (const it of items) {
    if (it.length == null) continue;
    const w = it.bbox.maxX - it.bbox.minX;
    const h = it.bbox.maxY - it.bbox.minY;
    if (Math.min(w, h) < minSide) continue;
    if (bboxDiagonal(it.bbox) < MIN_VIGNETTE_DIAGONAL_RATIO * sheetScale) {
      continue;
    }
    const perimeter = 2 * (w + h);
    if (Math.abs(it.length - perimeter) > FRAME_PERIMETER_TOLERANCE * perimeter) {
      continue;
    }
    if (isFullContent(w, h)) continue;
    // Un cadre de vue CONTIENT le dessin de sa vue — un cartouche de titre
    // (qui ne contient que son texte) n'est pas une vue.
    let contentCount = 0;
    for (const other of items) {
      if (other === it) continue;
      if (
        other.bbox.minX >= it.bbox.minX &&
        other.bbox.minY >= it.bbox.minY &&
        other.bbox.maxX <= it.bbox.maxX &&
        other.bbox.maxY <= it.bbox.maxY
      ) {
        contentCount++;
        if (contentCount >= FRAME_MIN_CONTENT_ITEMS) break;
      }
    }
    if (contentCount < FRAME_MIN_CONTENT_ITEMS) continue;
    rects.push({ bbox: it.bbox, area: w * h, children: [] });
  }
  // Cadres ASSEMBLÉS depuis les segments (grilles en lignes partagées) :
  // deux segments horizontaux de même étendue + un vertical à chaque bout.
  for (const bbox of assembleSegmentRects(items, sheetScale)) {
    const w = bbox.maxX - bbox.minX;
    const h = bbox.maxY - bbox.minY;
    if (Math.min(w, h) < minSide) continue;
    if (isFullContent(w, h)) continue;
    if (bboxDiagonal(bbox) < MIN_VIGNETTE_DIAGONAL_RATIO * sheetScale) continue;
    let contentCount = 0;
    for (const other of items) {
      if (
        other.bbox.minX >= bbox.minX &&
        other.bbox.minY >= bbox.minY &&
        other.bbox.maxX <= bbox.maxX &&
        other.bbox.maxY <= bbox.maxY &&
        bboxDiagonal(other.bbox) < 0.9 * bboxDiagonal(bbox)
      ) {
        contentCount++;
        if (contentCount >= FRAME_MIN_CONTENT_ITEMS) break;
      }
    }
    if (contentCount < FRAME_MIN_CONTENT_ITEMS) continue;
    rects.push({ bbox, area: w * h, children: [] });
  }
  if (rects.length === 0) return [];

  // Parent = le PLUS PETIT cadre qui contient le cadre (aires décroissantes :
  // les candidats parents d'un nœud le précèdent).
  rects.sort((a, b) => b.area - a.area);
  const epsilon = 0.002 * sheetScale;
  const roots: RectNode[] = [];
  for (let i = 0; i < rects.length; i++) {
    let parent: RectNode | null = null;
    for (let j = i - 1; j >= 0; j--) {
      if (containsRect(rects[j], rects[i], epsilon)) {
        if (!parent || rects[j].area < parent.area) parent = rects[j];
      }
    }
    if (parent) parent.children.push(rects[i]);
    else roots.push(rects[i]);
  }

  const boxes: WorldBbox[] = [];
  const resolve = (node: RectNode): void => {
    const cover = node.children.reduce((sum, c) => sum + c.area, 0);
    if (
      node.children.length >= 2 &&
      cover >= FRAME_TILE_COVER_RATIO * node.area
    ) {
      node.children.forEach(resolve);
    } else {
      boxes.push(node.bbox);
    }
  };
  roots.forEach(resolve);
  return boxes;
}

// ── Signal 2 : amas spatiaux (composantes connexes sur grille) ──────────────

interface ClusterBox {
  bbox: WorldBbox;
  /** L'amas contient-il autre chose que du texte (vrai contenu dessiné) ? */
  hasGraphics: boolean;
  /** Membres de l'amas — nécessaires au raffinement récursif. */
  items: BackgroundItem[];
}

function clusterBoxes(
  items: BackgroundItem[],
  planBbox: WorldBbox,
  sheetScale: number,
): ClusterBox[] {
  if (items.length === 0) return [];
  const cell = sheetScale * VIGNETTE_CELL_RATIO;

  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  const col = (x: number): number => Math.floor((x - planBbox.minX) / cell);
  const row = (y: number): number => Math.floor((y - planBbox.minY) / cell);
  const cellOwner = new Map<string, number>();
  const claim = (c: number, r: number, item: number): void => {
    const key = `${c}:${r}`;
    const owner = cellOwner.get(key);
    if (owner === undefined) cellOwner.set(key, item);
    else union(owner, item);
  };

  for (let i = 0; i < items.length; i++) {
    const b = items[i].bbox;
    const c0 = col(b.minX);
    const c1 = col(b.maxX);
    const r0 = row(b.minY);
    const r1 = row(b.maxY);
    const full = (c1 - c0 + 1) * (r1 - r0 + 1) <= MAX_CELLS_PER_ITEM;
    for (let c = c0; c <= c1; c++) {
      for (let r = r0; r <= r1; r++) {
        if (full || c === c0 || c === c1 || r === r0 || r === r1) {
          claim(c, r, i);
        }
      }
    }
  }
  // Connexité entre cellules voisines (8-voisinage via droite / bas / diag).
  for (const [key, owner] of cellOwner) {
    const [c, r] = key.split(":").map(Number);
    for (const [nc, nr] of [
      [c + 1, r],
      [c, r + 1],
      [c + 1, r + 1],
      [c + 1, r - 1],
    ]) {
      const neighbor = cellOwner.get(`${nc}:${nr}`);
      if (neighbor !== undefined) union(owner, neighbor);
    }
  }

  const byRoot = new Map<number, ClusterBox>();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    const isText = !!items[i].text?.trim();
    const entry = byRoot.get(root);
    if (entry) {
      entry.bbox = bboxUnion(entry.bbox, items[i].bbox);
      entry.hasGraphics = entry.hasGraphics || !isText;
      entry.items.push(items[i]);
    } else {
      byRoot.set(root, {
        bbox: items[i].bbox,
        hasGraphics: !isText,
        items: [items[i]],
      });
    }
  }
  return [...byRoot.values()];
}

/**
 * Raffinement récursif d'un amas : s'il contient PLUSIEURS textes de taille
 * « titre » (plusieurs vues cohabitent dedans — un groupe « Report
 * désordres » entier), on le re-clusterise à son échelle LOCALE, qui voit
 * les espaces que l'échelle de la feuille ne voyait pas. Un amas à titre
 * unique n'est jamais fragmenté.
 */
function refineCluster(
  cluster: ClusterBox,
  captionFloor: number,
  parentScale: number,
  depth: number,
): ClusterBox[] {
  if (depth >= MAX_REFINE_DEPTH) return [cluster];
  const textHeights = cluster.items
    .filter((it) => it.text?.trim())
    .map((it) => it.bbox.maxY - it.bbox.minY);
  if (textHeights.length < 2) return [cluster];
  const minCaption = Math.max(
    CAPTION_RELATIVE_HEIGHT * Math.max(...textHeights),
    captionFloor,
  );
  const captions = textHeights.filter((h) => h >= minCaption);
  if (captions.length < 2) return [cluster];

  const w = cluster.bbox.maxX - cluster.bbox.minX;
  const h = cluster.bbox.maxY - cluster.bbox.minY;
  const localScale = Math.sqrt(Math.max(0, w) * Math.max(0, h));
  // Pas de progrès d'échelle = re-clusteriser ne verra rien de plus.
  if (localScale <= 0 || localScale >= 0.9 * parentScale) return [cluster];

  const sub = clusterBoxes(cluster.items, cluster.bbox, localScale).filter(
    (c) => bboxDiagonal(c.bbox) >= MIN_VIGNETTE_DIAGONAL_RATIO * localScale,
  );
  if (sub.length < 2) return [cluster];
  return sub.flatMap((c) =>
    refineCluster(c, captionFloor, localScale, depth + 1),
  );
}

// ── Cascade de détection ────────────────────────────────────────────────────

/**
 * Détecte les vignettes depuis les emprises du fond de plan. Renvoie une
 * liste triée en ordre de lecture (gauche → droite, puis haut de feuille) —
 * les ids "v1", "v2"… sont stables pour UNE analyse donnée.
 */
export function detectVignettes(
  items: BackgroundItem[],
  planBbox: WorldBbox | null,
): PlanVignette[] {
  if (!planBbox || items.length === 0) return [];
  const planW = planBbox.maxX - planBbox.minX;
  const planH = planBbox.maxY - planBbox.minY;
  if (planW <= 0 && planH <= 0) return [];

  // Les éléments FILANTS (plus de VIGNETTE_FRAME_RATIO d'un axe) pontent
  // les vues : lignes de sol, chaînages, cadres de feuille — écartés. En
  // DEUX passes : les filants gonflent l'emprise de la feuille (vécu : une
  // ligne de chaînage de 5177 unités sur un contenu de 530), on les retire
  // donc d'abord à l'échelle brute, puis à l'échelle du CONTENU restant.
  const spanFilter = (
    list: BackgroundItem[],
    width: number,
    height: number,
  ): BackgroundItem[] =>
    list.filter((it) => {
      const w = it.bbox.maxX - it.bbox.minX;
      const h = it.bbox.maxY - it.bbox.minY;
      const long = Math.max(w, h);
      const short = Math.min(w, h);
      const spansX = w >= VIGNETTE_FRAME_RATIO * width;
      const spansY = h >= VIGNETTE_FRAME_RATIO * height;
      return !((spansX || spansY) && short <= SPANNER_THINNESS_RATIO * long);
    });
  const contentBboxOf = (list: BackgroundItem[]): WorldBbox =>
    list.slice(1).reduce((acc, it) => bboxUnion(acc, it.bbox), list[0].bbox);

  let usable = spanFilter(items, planW, planH);
  if (usable.length === 0) return [];
  let contentBbox = contentBboxOf(usable);
  usable = spanFilter(
    usable,
    contentBbox.maxX - contentBbox.minX,
    contentBbox.maxY - contentBbox.minY,
  );
  if (usable.length === 0) return [];
  contentBbox = contentBboxOf(usable);

  // L'échelle de feuille des seuils relatifs : moyenne géométrique de
  // l'emprise du CONTENU (insensible aux bandes allongées et aux feuilles
  // gonflées par un élément égaré).
  const sheetScale = Math.sqrt(
    (contentBbox.maxX - contentBbox.minX) *
      (contentBbox.maxY - contentBbox.minY),
  );
  if (sheetScale <= 0) return [];

  // Signal 1 : cadres (là où ce process de mise en page existe). Sur les
  // ITEMS BRUTS : les bords d'un cadre sont souvent des segments « filants »
  // au sens du clustering (fins, traversant la feuille) — exclus des amas,
  // mais indispensables à l'assemblage des cadres.
  const frames = frameVignetteBoxes(items, sheetScale, contentBbox);

  // Signal 2 : amas spatiaux sur le fond HORS cadres — les mises en page
  // sans cadres (vues simplement espacées) passent entièrement par lui.
  // Les boîtes quasi égales au contenu entier (bords de feuille épais) n'y
  // participent pas : leur anneau rasterisé engloberait tous les amas.
  const contentW = contentBbox.maxX - contentBbox.minX;
  const contentH = contentBbox.maxY - contentBbox.minY;
  const clusterable = usable.filter(
    (it) =>
      !(
        it.bbox.maxX - it.bbox.minX >= CLUSTER_FULL_CONTENT_RATIO * contentW &&
        it.bbox.maxY - it.bbox.minY >= CLUSTER_FULL_CONTENT_RATIO * contentH
      ),
  );
  const clusterSource = frames.length
    ? clusterable.filter((it) => !frames.some((f) => intersects(it.bbox, f)))
    : clusterable;
  const captionFloor = CAPTION_TEXT_FLOOR_RATIO * sheetScale;
  let clusters = clusterBoxes(clusterSource, contentBbox, sheetScale)
    .filter(
      (c) => bboxDiagonal(c.bbox) >= MIN_VIGNETTE_DIAGONAL_RATIO * sheetScale,
    )
    .flatMap((c) =>
      refineCluster(c, captionFloor, Number.POSITIVE_INFINITY, 0),
    );
  if (frames.length > 0) {
    // En présence de cadres, un reliquat purement textuel (titre de feuille)
    // n'est pas une vue ; et un amas MAJORITAIREMENT couvert par des cadres
    // en est la redite. (Mesuré sur l'aire de l'AMAS : un grand amas qui ne
    // fait que contenir un petit cadre reste une vue à part entière.)
    clusters = clusters.filter((c) => {
      if (!c.hasGraphics) return false;
      const area =
        (c.bbox.maxX - c.bbox.minX) * (c.bbox.maxY - c.bbox.minY) || 1;
      const covered = frames.reduce(
        (sum, f) => sum + intersectionArea(c.bbox, f),
        0,
      );
      return covered / area <= 0.5;
    });
  }

  const boxes: WorldBbox[] = [...frames, ...clusters.map((c) => c.bbox)];
  if (boxes.length === 0) return [];

  // Fusion des emprises qui se recouvrent (vue détectée en morceaux).
  let mergedSomething = true;
  while (mergedSomething) {
    mergedSomething = false;
    for (let i = 0; i < boxes.length && !mergedSomething; i++) {
      for (let j = i + 1; j < boxes.length && !mergedSomething; j++) {
        if (overlapRatio(boxes[i], boxes[j]) > VIGNETTE_MERGE_OVERLAP) {
          boxes[i] = bboxUnion(boxes[i], boxes[j]);
          boxes.splice(j, 1);
          mergedSomething = true;
        }
      }
    }
  }

  // Titres : le plus grand texte du fond dans (ou contre) chaque vignette.
  const captionMargin = VIGNETTE_CAPTION_MARGIN_RATIO * sheetScale;
  const texts = usable.filter((it) => it.text?.trim());
  const captionFor = (bbox: WorldBbox): string | null => {
    // Critère premier : la PART DE L'AIRE du texte contenue dans la
    // vignette — un titre à cheval sur un trait de cellule déborde de
    // quelques dixièmes sur la cellule voisine (vécu), et un MTEXT fantôme
    // à colonne démesurée touche tout le monde : seule la part contenue
    // départage proprement. Puis le plus grand, puis le plus proche du
    // bord HAUT (convention des titres de cellules).
    let best: BackgroundItem | null = null;
    let bestRatio = -1;
    let bestHeight = 0;
    let bestTopDist = Number.POSITIVE_INFINITY;
    for (const t of texts) {
      const gap = bboxGap(t.bbox, bbox);
      if (gap > captionMargin) continue;
      const area =
        (t.bbox.maxX - t.bbox.minX) * (t.bbox.maxY - t.bbox.minY);
      const insideRatio =
        area > 0 ? intersectionArea(t.bbox, bbox) / area : 0;
      const height = t.bbox.maxY - t.bbox.minY;
      const topDist = Math.abs(
        bbox.maxY - (t.bbox.minY + t.bbox.maxY) / 2,
      );
      const sameRatio = Math.abs(insideRatio - bestRatio) <= 1e-9;
      const better =
        (!sameRatio && insideRatio > bestRatio) ||
        (sameRatio &&
          (height > bestHeight ||
            (height === bestHeight && topDist < bestTopDist)));
      if (better) {
        best = t;
        bestRatio = insideRatio;
        bestHeight = height;
        bestTopDist = topDist;
      }
    }
    return best?.text?.trim().slice(0, 80) ?? null;
  };

  // Ordre de lecture : colonne par colonne (gauche → droite), haut → bas.
  boxes.sort((a, b) => (a.minX !== b.minX ? a.minX - b.minX : b.maxY - a.maxY));
  return boxes.map((bbox, index) => ({
    id: `v${index + 1}`,
    bbox,
    diagonal: bboxDiagonal(bbox),
    name: captionFor(bbox),
  }));
}

/** Points d'ancrage d'une entité : pointes (leaders/flèche), sinon centre. */
function anchorPoints(entity: AnalysisEntity): Array<{ x: number; y: number }> {
  if (entity.leaderTips?.length) return entity.leaderTips;
  if (entity.markerTip) return [entity.markerTip];
  if (!entity.bbox) return [];
  return [
    {
      x: (entity.bbox.minX + entity.bbox.maxX) / 2,
      y: (entity.bbox.minY + entity.bbox.maxY) / 2,
    },
  ];
}

/**
 * Vignette d'appartenance d'une entité : celle qui contient le plus de ses
 * ancres (pointes de leaders ou de flèche — c'est LÀ que l'entité pointe —
 * sinon centre), avec une petite marge ; à défaut la plus proche. Null sans
 * vignettes.
 */
export function vignetteIdForEntity(
  entity: AnalysisEntity,
  vignettes: PlanVignette[],
  planDiagonal: number,
): string | null {
  if (vignettes.length === 0) return null;
  const points = anchorPoints(entity);
  if (points.length === 0) return vignettes[0].id;

  const margin = VIGNETTE_CONTAIN_MARGIN_RATIO * planDiagonal;
  const pointBoxes: WorldBbox[] = points.map((p) => ({
    minX: p.x,
    minY: p.y,
    maxX: p.x,
    maxY: p.y,
  }));
  // Score = nb d'ancres contenues (avec marge) ; départage par la distance
  // au bord RÉEL — deux vignettes adjacentes peuvent toutes deux « contenir »
  // une ancre via la marge, la plus proche l'emporte alors. Sans contenance
  // du tout (entre deux vues, marge de feuille), la plus proche gagne aussi.
  let best = vignettes[0];
  let bestContained = -1;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const v of vignettes) {
    const contained = points.filter(
      (p) =>
        p.x >= v.bbox.minX - margin &&
        p.x <= v.bbox.maxX + margin &&
        p.y >= v.bbox.minY - margin &&
        p.y <= v.bbox.maxY + margin,
    ).length;
    const gap = Math.min(...pointBoxes.map((p) => bboxGap(p, v.bbox)));
    if (
      contained > bestContained ||
      (contained === bestContained && gap < bestGap)
    ) {
      best = v;
      bestContained = contained;
      bestGap = gap;
    }
  }
  return best.id;
}
